// PickUpDraft.js
// Pick Up Draft — the multiplayer draft screen. Reads its gameID from ?gameID= (or
// location.state.gameID), attaches a live Firestore listener, and renders the shared
// PickUpDraftBoard. Picks go through the processPickUpTurn Cloud Function — on success the
// listener delivers the advanced game, so there's no optimistic local mutation. Auto-navigates
// to the complete screen when the game finishes (matching iOS), or back to the hub on forfeit.
//
// The visual board lives in components/PickUpDraftBoard.js and is shared with the solo Play
// CPU screen (PickUpCpu.js); this file owns only the Firestore / turn plumbing.
//
// Reachable at /battles/pickup/draft?gameID=<id>.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import PickUpDraftBoard from "../components/PickUpDraftBoard";
import {
  listenToPickUpGame,
  processPickUpTurn,
  callableErrorMessage,
} from "../managers/pickupService";

export default function PickUpDraft() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const gameID = searchParams.get("gameID") || location.state?.gameID || null;

  const [game, setGame] = useState(null);
  const [toast, setToast] = useState("");

  const navigatedRef = useRef(false);
  const unsubRef = useRef(null);

  const flash = useCallback((m) => {
    setToast(m);
    window.setTimeout(() => setToast(""), 3500);
  }, []);

  // Live listener.
  useEffect(() => {
    if (!gameID) return undefined;
    unsubRef.current = listenToPickUpGame(gameID, (g) => {
      if (g) setGame(g);
    });
    return () => {
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = null;
    };
  }, [gameID]);

  // Auto-navigate on terminal status (mirrors iOS: 0.6s to complete, 0.3s to hub on forfeit).
  useEffect(() => {
    if (!game || navigatedRef.current) return;
    if (game.status === "complete") {
      navigatedRef.current = true;
      window.setTimeout(() => {
        navigate(`/battles/pickup/complete?gameID=${game.id}`, { state: { gameID: game.id } });
      }, 600);
    } else if (game.status === "forfeited") {
      navigatedRef.current = true;
      window.setTimeout(() => navigate("/battles"), 300);
    }
  }, [game, navigate]);

  // Human pick → advance the turn via the Cloud Function; the listener delivers the result.
  const submit = async (position, slotInRound, player) => {
    try {
      await processPickUpTurn({
        gameID: game.id,
        roundIndex: game.currentRoundIndex,
        position,
        slotInRound,
        selectedPlayerName: player.name,
      });
    } catch (e) {
      flash(callableErrorMessage(e));
    }
  };

  if (!gameID) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>No game specified.</p>
      </div>
    );
  }

  if (!game) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>Loading draft…</p>
      </div>
    );
  }

  return (
    <PickUpDraftBoard
      game={game}
      uid={uid}
      onPick={submit}
      onBack={() => navigate("/battles")}
      showTimer
      toast={toast}
    />
  );
}

const styles = {
  page: { padding: "16px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh" },
  dim: { fontSize: 14, color: "#6e747e", textAlign: "center", marginTop: 40 },
};
